import { useRef, useCallback, useEffect } from 'react'
import { getApiBaseUrl } from './useFunnelConfig'

interface AnalyticsSession {
  sessionId: string | null
  funnelId: string
  interactionCount: number
  categoriesTouched: Set<string>
  colorFilterUsed: string | null
  questionAnswer: 'yes' | 'no' | null
}

export function useConfiguratorAnalytics(funnelId: string | undefined) {
  const sessionRef = useRef<AnalyticsSession>({
    sessionId: null,
    funnelId: '',
    interactionCount: 0,
    categoriesTouched: new Set(),
    colorFilterUsed: null,
    questionAnswer: null,
  })

  const hasStartedRef = useRef(false)

  // Start session
  const startSession = useCallback(async (urlConfig?: number[]) => {
    if (!funnelId || hasStartedRef.current) return

    hasStartedRef.current = true
    sessionRef.current.funnelId = funnelId

    const device = window.innerWidth < 1024 ? 'mobile' : 'desktop'

    try {
      const baseUrl = getApiBaseUrl()
      const res = await fetch(`${baseUrl}/api/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'start',
          funnelId,
          device,
          referrer: document.referrer || null,
          urlConfig: urlConfig && urlConfig.length > 0 ? urlConfig : null,
        }),
      })

      if (res.ok) {
        const { sessionId } = await res.json()
        sessionRef.current.sessionId = sessionId
      }
    } catch (e) {
      // Silent fail - analytics should not break the app
    }
  }, [funnelId])

  // Track interaction (product selection)
  const trackInteraction = useCallback((categoryId: string) => {
    sessionRef.current.interactionCount++
    sessionRef.current.categoriesTouched.add(categoryId)
  }, [])

  // Track color filter usage
  const trackColorFilter = useCallback((tag: string | null) => {
    sessionRef.current.colorFilterUsed = tag
  }, [])

  // Track question answer
  const trackQuestionAnswer = useCallback((answer: 'yes' | 'no') => {
    sessionRef.current.questionAnswer = answer
  }, [])

  // End session
  const endSession = useCallback(async (completed: boolean, finalConfig?: number[], totalPrice?: number) => {
    if (!sessionRef.current.sessionId) return

    try {
      const baseUrl = getApiBaseUrl()
      await fetch(`${baseUrl}/api/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'end',
          sessionId: sessionRef.current.sessionId,
          completed,
          finalConfig,
          totalPrice,
          interactionCount: sessionRef.current.interactionCount,
          categoriesTouched: Array.from(sessionRef.current.categoriesTouched),
          colorFilterUsed: sessionRef.current.colorFilterUsed,
          questionAnswer: sessionRef.current.questionAnswer,
        }),
      })
    } catch (e) {
      console.error('Failed to end analytics session:', e)
    }

    // Reset
    sessionRef.current.sessionId = null
  }, [])

  // End session on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (sessionRef.current.sessionId) {
        // Use sendBeacon with Blob for proper Content-Type
        const data = JSON.stringify({
          action: 'end',
          sessionId: sessionRef.current.sessionId,
          completed: false,
          interactionCount: sessionRef.current.interactionCount,
          categoriesTouched: Array.from(sessionRef.current.categoriesTouched),
          colorFilterUsed: sessionRef.current.colorFilterUsed,
          questionAnswer: sessionRef.current.questionAnswer,
        })
        const blob = new Blob([data], { type: 'application/json' })
        const baseUrl = getApiBaseUrl()
        navigator.sendBeacon(`${baseUrl}/api/analytics/track`, blob)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  return {
    startSession,
    trackInteraction,
    trackColorFilter,
    trackQuestionAnswer,
    endSession,
  }
}
